import axios from "axios";
import { BaseExporter } from "../BaseExporter";
import { KiCadConverter } from "../KicadPcbExporter";
/**
 * Join URL paths, handling trailing/leading slashes correctly.
 */
function joinUrlPaths(base, path) {
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
}
/**
 * Error class for fabrication file export errors.
 * Defined locally to avoid boundary rule violations with flux_error module.
 */
export class FabricationExportError extends Error {
    constructor(message) {
        super(message);
        this.name = "FabricationExportError";
    }
}
/**
 * Endpoint paths for the fabrication file export service.
 */
export const FABRICATION_SERVICE_ENDPOINTS = {
    GERBER: "/gerber",
    NETLIST: "/netlist",
    STEP: "/step",
    IPC2581C: "/ipc2581c",
    DRC: "/drc",
    ODB: "/odb",
    // New KiCad 9 export formats
    BREP: "/brep",
    DXF: "/dxf",
    GENCAD: "/gencad",
    GLB: "/glb",
    PDF: "/pdf",
    POS: "/pos",
    SVG: "/svg",
    VRML: "/vrml",
    XAO: "/xao",
};
/**
 * ManufacturingFilesExporter - generates parameters for manufacturing file exports.
 * This class handles the conversion of PCB layout data to KiCad format,
 * which is then used to generate various manufacturing file formats
 * (Gerber, IPC-D-356, STEP, IPC-2581C, DRC, ODB++).
 */
export class ManufacturingFilesExporter extends BaseExporter {
    activePcbLayoutNodes;
    services;
    constructor(documentData, userData, activePcbLayoutNodes, services) {
        super(documentData, userData);
        this.activePcbLayoutNodes = activePcbLayoutNodes;
        this.services = services;
    }
    generateExportParams() {
        const fillShapes = this.services.pcbPrimitiveStore.getFillShapesMap();
        const converter = new KiCadConverter(this.activePcbLayoutNodes, this.services.logger, fillShapes, this.services.pcbTracingGraph);
        const { kicadString, layersForGerberExport } = converter.convert();
        return {
            layers: layersForGerberExport,
            kicad: kicadString,
            filename: this.documentData.slug,
        };
    }
    /**
     * Generate KiCad PCB file for direct download (frontend use case).
     * This method is used by the frontend's KicadExporter to generate
     * .kicad_pcb files for direct user download.
     */
    generateKicadPcb() {
        const { kicad } = this.generateExportParams();
        return { kicadString: kicad };
    }
}
/**
 * Build manufacturing export parameters from the given export params using the new KiCad export path.
 */
export function buildManufacturingExportParams(params) {
    const exporter = new ManufacturingFilesExporter(params.documentData, params.userData, params.activePcbLayoutNodes, {
        logger: params.logger,
        pcbPrimitiveStore: params.pcbPrimitiveStore,
        pcbTracingGraph: params.tracingGraph,
    });
    return exporter.generateExportParams();
}
/**
 * FabricationFileExporter - handles all export requests to the fabrication file export service.
 * This class encapsulates all the business logic for exporting PCB data to various formats.
 * Pass IExportParams at construction time, then call export methods with no arguments.
 */
export class FabricationFileExporter {
    exportParams;
    config;
    manufacturingParams;
    constructor(exportParams, config) {
        this.exportParams = exportParams;
        this.config = config;
        this.manufacturingParams = buildManufacturingExportParams(exportParams);
    }
    /**
     * Get the manufacturing export params (for advanced use cases).
     */
    getManufacturingParams() {
        return this.manufacturingParams;
    }
    /**
     * Export Gerber files (.zip)
     */
    async exportGerber() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.GERBER, "Gerber");
    }
    /**
     * Export IPC-D-356 netlist (.d356)
     */
    async exportIpcD356() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.NETLIST, "IPC-D356");
    }
    /**
     * Export STEP 3D model (.step)
     */
    async exportStep() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.STEP, "STEP");
    }
    /**
     * Export IPC-2581C (.ipc2581c)
     */
    async exportIpc2581c() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.IPC2581C, "IPC-2581C");
    }
    /**
     * Export DRC report (.json)
     */
    async exportDrc() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.DRC, "DRC");
    }
    /**
     * Export ODB++ (.odb)
     */
    async exportOdb() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.ODB, "ODB++");
    }
    /**
     * Export BREP (OCCT) 3D model (.brep)
     */
    async exportBrep() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.BREP, "BREP");
    }
    /**
     * Export DXF vector format (.dxf)
     */
    async exportDxf() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.DXF, "DXF");
    }
    /**
     * Export GenCAD format (.cad)
     */
    async exportGencad() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.GENCAD, "GenCAD");
    }
    /**
     * Export GLB (binary glTF) 3D model (.glb)
     */
    async exportGlb() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.GLB, "GLB");
    }
    /**
     * Export PDF (.pdf)
     */
    async exportPdf() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.PDF, "PDF");
    }
    /**
     * Export position file (.csv)
     */
    async exportPos() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.POS, "Position");
    }
    /**
     * Export SVG vector format (.svg)
     */
    async exportSvg() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.SVG, "SVG");
    }
    /**
     * Export VRML 3D model (.wrl)
     */
    async exportVrml() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.VRML, "VRML");
    }
    /**
     * Export XAO format (.xao)
     */
    async exportXao() {
        return this.fetchExportResponse(FABRICATION_SERVICE_ENDPOINTS.XAO, "XAO");
    }
    /**
     * Export KiCad PCB file (.kicad_pcb)
     * Unlike other export methods, this returns the locally-generated KiCad string as a Blob
     * without calling the remote fabrication service.
     */
    exportKicadPcb() {
        return new Blob([this.manufacturingParams.kicad], { type: "text/plain" });
    }
    /**
     * Detect if we're running in a Node.js environment (vs browser).
     */
    isNodeEnvironment() {
        return typeof process !== "undefined" && process.versions != null && process.versions.node != null;
    }
    /**
     * Generic method to fetch export response from the fabrication file export service.
     * Uses blob responseType in browsers (known-good) and arraybuffer in Node.js for consistent behavior.
     */
    async fetchExportResponse(endpoint, formatName) {
        const url = joinUrlPaths(this.config.baseUrl, endpoint);
        const isNode = this.isNodeEnvironment();
        try {
            // Use blob in browsers (known-good), arraybuffer in Node.js (fixes Copilot backend)
            const response = await axios.post(url, this.manufacturingParams, {
                responseType: isNode ? "arraybuffer" : "blob",
            });
            const data = response.data;
            // In Node.js with arraybuffer, validate response to detect error responses
            if (isNode) {
                const contentType = response.headers["content-type"];
                if (contentType && (contentType.includes("application/json") || contentType.includes("text/html"))) {
                    // Try to extract error message from the response
                    let errorMessage = `${formatName} service returned an error`;
                    try {
                        const textDecoder = new TextDecoder("utf-8");
                        const text = textDecoder.decode(data);
                        // Try to parse as JSON first
                        try {
                            const parsed = JSON.parse(text);
                            if (typeof parsed === "object" && parsed !== null) {
                                const obj = parsed;
                                if (typeof obj.error === "string") {
                                    errorMessage = obj.error;
                                }
                                else if (typeof obj.message === "string") {
                                    errorMessage = obj.message;
                                }
                                else {
                                    errorMessage = text.substring(0, 500);
                                }
                            }
                        }
                        catch {
                            // Not JSON, use the text directly (truncated)
                            errorMessage = text.substring(0, 500);
                        }
                    }
                    catch {
                        // Ignore decoding errors
                    }
                    throw new FabricationExportError(errorMessage);
                }
                // Convert ArrayBuffer/Buffer to Blob in Node.js
                if (data instanceof ArrayBuffer) {
                    return new Blob([data]);
                }
                if (typeof Buffer !== "undefined" && data instanceof Buffer) {
                    return new Blob([data]);
                }
            }
            // Browser environment - already a Blob
            if (data instanceof Blob) {
                return data;
            }
            // Polyfilled Blob-like object (has arrayBuffer method)
            if (data && typeof data.arrayBuffer === "function") {
                return data;
            }
            // Unexpected type - throw error instead of corrupting binary data
            const typeTag = Object.prototype.toString.call(data);
            let debugInfo = "";
            try {
                debugInfo = `, payload: ${JSON.stringify(data)}`;
            }
            catch {
                // Ignore serialization errors
            }
            throw new FabricationExportError(`Unexpected response type from ${formatName} service: ${typeTag}${debugInfo}`);
        }
        catch (error) {
            if (error instanceof FabricationExportError) {
                throw error;
            }
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            throw new FabricationExportError(`Error returned from ${formatName} service: ${errorMessage}`);
        }
    }
}
/**
 * Create a fabrication file exporter with the given parameters and configuration.
 * This is the main entry point for creating an exporter.
 *
 * @param exportParams - The export parameters including PCB data
 * @param config - Configuration with the base URL for the fabrication service
 */
export function createFabricationFileExporter(exportParams, config) {
    return new FabricationFileExporter(exportParams, config);
}
//# sourceMappingURL=ManufacturingFilesExporter.js.map